import React, { useState, useEffect, useMemo } from "react";
import styled from "@emotion/styled";
import useMedia from "react-use/lib/useMedia";
import { Element as ScrollElement, scroller } from "react-scroll";
import { boolean, object, string } from "yup";
import { useForm } from "../utils/use-form";
import {
  Location,
  countryOptions,
  Country,
  RedeemGiftPayload,
} from "@ecologi/shared";
import HeaderSvg from "@ecologi/assets/icons/header_tag_redeem.svg";
import classnames from "classnames";
import { Select } from "../Select";
import { Wrapper } from "../Wrapper";
import { Button } from "../Button";
import { GiftingPage } from "../GiftingPage";
import { TermsAcceptance } from "../TermsAcceptance";
import { ChallengeCodeInput } from "../ChallengeCodeInput";
import { TextField } from "../TextField";
import styles from "./GiftRedeemPage.module.scss";
import { FormLayout, RowItem, RowItems } from "../FormLayout";
import { FormFooter } from "../FormFooter";

const Title = GiftingPage.Title;
const Text = GiftingPage.Text;
const ContentContainer = GiftingPage.ContentContainer;

const ResponsiveHeaderTag = styled(HeaderSvg)`
  width: 368px;

  margin-top: -60px;
  margin-right: -91px;

  @media ${(props) => props.theme.bp.mobileUp} {
    margin-top: -40px;
    margin-right: 0;
  }

  @media ${(props) => props.theme.bp.tabletUp} {
    width: 470px;
    margin-top: -140px;
    margin-right: -50px;
  }
`;

const CodeChallengeRow = styled.div`
  margin-top: 15px;
  display: flex;
  justify-content: center;
  margin-bottom: 30px;
`;

const ButtonRow = styled.div`
  display: flex;
  justify-content: center;
`;

const GiftCodeRow = styled.div`
  padding-top: 60px;
  padding-bottom: 83px;
  background-color: ${(props) => props.theme.colors.mist};
`;

const CenteredText = styled(Text)`
  max-width: 580px;
  margin: 10px auto;
`;

const Form = styled.form`
  margin: 30px auto;
  max-width: 365px;
`;

const FormFooterContainer = styled.div`
  margin-top: 3rem;
`;

export type CouponCodeRecord = { country?: Country };

type Props = {
  location: Location;
  couponCodeRecord: CouponCodeRecord | undefined; // has not yet been found
  onSubmitRedeemGift: (data: RedeemGiftPayload) => Promise<any>;
  onSubmitCouponCode: (couponCode: string) => Promise<any>;
  openChatWidget: () => void;
};

const LENGTH_OF_COUPON_CODE = 9;

const SCROLLER_ELEMENT_NAME = "sign-up-form";

export type RedeemFormFields = {
  email: string;
  password: string;
  firstName: string;
  lastName: string;
  country: Country;
  isTermsAndConditionsAccepted: boolean;
  isMarketingAccepted: boolean;
};

const getSchema = (countryDefault: Country) =>
  object().shape({
    email: string()
      .default("")
      .email("Please use a valid email address")
      .required("Email address is a required field"),
    password: string()
      .default("")
      .min(8, "Please use 8 or more characters")
      .required("Password is a required field"),
    firstName: string().default("").required(),
    country: string().default(countryDefault).required(),
    lastName: string().default("").required(),
    isTermsAndConditionsAccepted: boolean().default(false).isTrue(),
    isMarketingAccepted: boolean().default(false).isTrue(),
  });

export const GiftRedeemPage = ({
  location,
  onSubmitRedeemGift,
  onSubmitCouponCode,
  couponCodeRecord,
  openChatWidget,
}: Props) => {
  const [couponValues, setCouponValues] = useState(
    Array(LENGTH_OF_COUPON_CODE).fill("")
  );

  useEffect(() => {
    if (couponValues.join("").length === 9) {
      const couponCode = couponValues.join("");
      onSubmitCouponCode(couponCode);
    }
  }, [couponValues, onSubmitCouponCode]);

  const isAboveMobile = useMedia(
    `only screen and (min-width: ${styles.mobileBreakpoint})`
  );

  const country = (
    location
      ? location.countryCode !== "EU"
        ? (location.countryCode as string)
        : "GB"
      : "US"
  ) as Country;

  const schema = useMemo(() => getSchema(country), [location]);

  const form = useForm<RedeemFormFields>({
    validationSchema: schema,
    initialValues: ((initial) => ({
      ...initial,
      country: initial.country as Country,
    }))(schema.getDefaultFromShape()),
    onSubmit: async (values) => {
      const {
        isTermsAndConditionsAccepted,
        isMarketingAccepted,
        ...restFormValues
      } = values;

      setLoadingState(true);

      await onSubmitRedeemGift({
        ...restFormValues,
        location: restFormValues.country,
        couponCode: couponValues.join(""),
      });

      setLoadingState(false);
    },
  });

  const [isLoading, setLoadingState] = useState(false);
  const [showForm, setShowForm] = useState(false);

  return (
    <GiftingPage headerContent={<ResponsiveHeaderTag />}>
      <GiftCodeRow className={styles.giftCodeSection}>
        <Wrapper isLarge>
          <Title style={{ marginBottom: "20px" }}>Enter your gift code</Title>
          <Text style={{ maxWidth: "680px", margin: "0 auto 30px" }}>
            Ecologi's Gifting service has now closed and no new vouchers can be
            purchased. You can still redeem existing codes up to 31st October
            2024.
            <br />
            <br />
            If you’re already an Ecologi member, please{" "}
            <a
              href="#help-chat"
              className="link"
              onClick={(e) => {
                e.preventDefault();
                openChatWidget();
              }}
            >
              drop us a message
            </a>{" "}
            containing your code and we'll apply your gift directly to your
            account.
          </Text>
          <CodeChallengeRow
            // Work around to make the Polaris input to be responsive for mobile viewport
            className={classnames({ ["code-challenge-row"]: !isAboveMobile })}
          >
            {isAboveMobile ? (
              <ChallengeCodeInput
                values={couponValues}
                onChange={(evt) => {
                  setCouponValues(evt.target.value);
                }}
              />
            ) : (
              <TextField
                name="Coupon Code"
                label="Coupon code"
                onChange={(event) => {
                  const couponCodeValues = event.target.value.split("");
                  setCouponValues(couponCodeValues);
                }}
                maxLength={LENGTH_OF_COUPON_CODE}
                id="coupon-code"
                value={couponValues.join("")}
              />
            )}
          </CodeChallengeRow>
          <ButtonRow>
            <Button
              disabled={
                couponValues.join("").length < 9 ||
                (couponValues.join("").length >= 9 && !couponCodeRecord)
              }
              onClick={() => {
                setShowForm(true);

                setTimeout(() => {
                  scroller.scrollTo(SCROLLER_ELEMENT_NAME, {
                    duration: 1000,
                    offset: -40,
                    smooth: true,
                  });
                }, 500);
              }}
              primary
              text="Continue"
            />
          </ButtonRow>
        </Wrapper>
      </GiftCodeRow>
      {showForm && (
        <ScrollElement name={SCROLLER_ELEMENT_NAME}>
          <ContentContainer gutterLarge>
            <Title>Next, we need a few details</Title>
            <CenteredText>
              We need a few details to set up your profile. From here you can
              track your progress and see your positive environmental impact!
            </CenteredText>
            <Form onSubmit={form.handleSubmit}>
              <FormLayout>
                <RowItems>
                  <RowItem>
                    <TextField
                      {...form.getFieldProps("email", "Email address")}
                      type="email"
                      errorText={form.errors.email?.toString()}
                      id="email"
                    />
                  </RowItem>
                  <RowItem>
                    <TextField
                      {...form.getFieldProps("password", "Password")}
                      type="password"
                      errorText={form.errors.password?.toString()}
                      id="password"
                    />
                  </RowItem>
                  <RowItem>
                    <TextField
                      {...form.getFieldProps("firstName", "First name")}
                      id="firstName"
                    />
                  </RowItem>
                  <RowItem>
                    <TextField
                      {...form.getFieldProps("lastName", "Last name")}
                      id="lastName"
                    />
                  </RowItem>

                  {couponCodeRecord && !couponCodeRecord.country && (
                    <RowItem>
                      <Select
                        {...form.getFieldProps("country", "Country")}
                        items={countryOptions}
                        data-testid="country-select"
                        id="country"
                      />
                    </RowItem>
                  )}
                </RowItems>
                <RowItems>
                  <RowItem>
                    <TermsAcceptance
                      isLoading={isLoading}
                      isTermsChecked={
                        form.values["isTermsAndConditionsAccepted"]
                      }
                      isEmailTermsChecked={form.values["isMarketingAccepted"]}
                      setIsTermsChecked={(isChecked) => {
                        form.handleChange({
                          target: {
                            name: "isTermsAndConditionsAccepted",
                            value: isChecked,
                          },
                        });
                      }}
                      setIsEmailTermsChecked={(isChecked) =>
                        form.handleChange({
                          target: {
                            name: "isMarketingAccepted",
                            value: isChecked,
                          },
                        })
                      }
                      disableEmailCheckbox
                    />
                  </RowItem>
                </RowItems>
              </FormLayout>
              <ButtonRow>
                <Button
                  disabled={!form.isValid || form.isSubmitting || !form.dirty}
                  primary
                  loading={isLoading}
                  blockOnMobile
                  submit
                  text="Redeem gift"
                />
              </ButtonRow>
              <FormFooterContainer>
                <FormFooter />
              </FormFooterContainer>
            </Form>
          </ContentContainer>
        </ScrollElement>
      )}
    </GiftingPage>
  );
};
